import { useEffect, useRef } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AdminLayout } from '@/pages/AdminLayout'
import { AdminDashboard } from '@/pages/AdminDashboard'
import { UserManagement } from '@/pages/UserManagement'
import { OrganizationManagement } from '@/pages/OrganizationManagement'
import { OrganizationDetailPage } from '@/pages/OrganizationDetailPage'
import { SubscriptionManagement } from '@/pages/SubscriptionManagement'
import { OrgSubscriptionManagement } from '@/pages/OrgSubscriptionManagement'
import { ProductManagement } from '@/pages/ProductManagement'
import { ContentManagement } from '@/pages/ContentManagement'
import { ConfigurationManagement } from '@/pages/ConfigurationManagement'
import { NotesPage } from '@/pages/NotesPage'
import { SharedMediaLibraryPage } from '@/pages/SharedMediaLibraryPage'
import { LoginPage } from '@/pages/LoginPage'
import { AnnouncementListPage, AnnouncementEditPage, AnnouncementDetailPage } from '@/pages/announcements'
import { SupportInquiryPage } from '@/pages/support'
import { DataStatsPage } from '@/pages/DataStatsPage'
import { LearningStatusPage } from '@/pages/LearningStatusPage'
import { BookListPage } from '@/pages/books/BookListPage'
import { BookEditPage } from '@/pages/books/BookEditPage'
import { CurriculumBuilderPage } from '@/pages/CurriculumBuilderPage'
import { ContentTabLayout } from '@/pages/ContentTabLayout'
import { LessonPreviewPage } from '@/pages/books/LessonPreviewPage'
import { AnswerKeyPreviewPage } from '@/pages/books/AnswerKeyPreviewPage'
import QuetureGradeManagement from '@/pages/QuetureGradeManagement'
import { RoleManagement } from '@/pages/RoleManagement'
import { useAuthStore } from '@/store/authStore'

function PermissionRoute({ permission, children }: { permission: string; children: React.ReactNode }) {
  const { hasPermission } = useAuthStore()

  if (!hasPermission(permission)) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function SuperRoute({ children }: { children: React.ReactNode }) {
  const { isSuper } = useAuthStore()

  if (!isSuper()) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, isLoading, hasCheckedAuth } = useAuthStore()

  // 인증 확인이 완료되지 않았거나 확인 중이면 로딩 표시
  if (!hasCheckedAuth || isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-900">
        <div className="text-center">
          <div className="inline-block w-8 h-8 rounded-full border-b-2 animate-spin border-amber-500"></div>
          <p className="mt-4 text-slate-400">인증 확인 중...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (user?.userType !== 'admin') {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-900">
        <div className="p-8 max-w-md text-center bg-white rounded-xl">
          <h1 className="mb-4 text-2xl font-bold text-slate-900">접근 권한 없음</h1>
          <p className="mb-6 text-slate-600">관리자 권한이 필요합니다.</p>
          <button
            onClick={() => useAuthStore.getState().logout()}
            className="btn-primary btn-md"
          >
            로그아웃
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

// Public Only Route - 로그인 상태에서 접근 시 메인으로 리다이렉트
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, hasCheckedAuth } = useAuthStore()

  if (!hasCheckedAuth) return null

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function App() {
  const { checkAuth, isHydrated, setHydrated } = useAuthStore()
  const hasInitialized = useRef(false)

  // 1. Rehydration 설정
  useEffect(() => {
    setHydrated()
  }, [setHydrated])

  // 2. 인증 초기화 - isHydrated가 true일 때 checkAuth 실행
  useEffect(() => {
    if (!isHydrated || hasInitialized.current) return
    hasInitialized.current = true
    checkAuth()
  }, [isHydrated, checkAuth])

  // 3. 로딩 상태 처리
  if (!isHydrated) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-900">
        <div className="text-center">
          <div className="inline-block w-8 h-8 rounded-full border-b-2 animate-spin border-amber-500"></div>
          <p className="mt-4 text-slate-400">시스템 초기화 중...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PermissionRoute permission="DASHBOARD"><AdminDashboard /></PermissionRoute>} />
        <Route path="users" element={<PermissionRoute permission="USERS"><UserManagement /></PermissionRoute>} />
        <Route path="organizations" element={<PermissionRoute permission="ORGANIZATIONS"><OrganizationManagement /></PermissionRoute>} />
        <Route path="organizations/:id" element={<PermissionRoute permission="ORGANIZATIONS"><OrganizationDetailPage /></PermissionRoute>} />
        <Route path="subscriptions/org" element={<PermissionRoute permission="SUBSCRIPTIONS_ORG"><OrgSubscriptionManagement /></PermissionRoute>} />
        <Route path="subscriptions/personal" element={<PermissionRoute permission="SUBSCRIPTIONS_PERSONAL"><SubscriptionManagement /></PermissionRoute>} />
        <Route path="subscriptions" element={<PermissionRoute permission="SUBSCRIPTIONS_ORG"><OrgSubscriptionManagement /></PermissionRoute>} />
        <Route path="products" element={<PermissionRoute permission="PRODUCTS"><ProductManagement /></PermissionRoute>} />
        <Route path="contents" element={<PermissionRoute permission="CONTENTS"><ContentTabLayout /></PermissionRoute>}>
          <Route index element={<ContentManagement />} />
          <Route path="curriculum-builder" element={<CurriculumBuilderPage />} />
          <Route path="curriculum-builder/:curriculumId" element={<CurriculumBuilderPage />} />
          <Route path="queture-contents" element={<QuetureGradeManagement />} />
          <Route path="books" element={<BookListPage />} />
          <Route path="books/new" element={<BookEditPage />} />
          <Route path="books/:id" element={<BookEditPage />} />
          <Route path="books/:bookId/preview/:lessonId" element={<LessonPreviewPage />} />
          <Route path="books/:bookId/answer-key/:lessonId" element={<AnswerKeyPreviewPage />} />
        </Route>
        <Route path="configurations" element={<PermissionRoute permission="CONFIGURATIONS"><ConfigurationManagement /></PermissionRoute>} />
        <Route path="announcements" element={<PermissionRoute permission="ANNOUNCEMENTS"><AnnouncementListPage /></PermissionRoute>} />
        <Route path="announcements/new" element={<PermissionRoute permission="ANNOUNCEMENTS"><AnnouncementEditPage /></PermissionRoute>} />
        <Route path="announcements/:id" element={<PermissionRoute permission="ANNOUNCEMENTS"><AnnouncementDetailPage /></PermissionRoute>} />
        <Route path="announcements/:id/edit" element={<PermissionRoute permission="ANNOUNCEMENTS"><AnnouncementEditPage /></PermissionRoute>} />
        <Route path="support-inquiries" element={<PermissionRoute permission="SUPPORT_INQUIRIES"><SupportInquiryPage /></PermissionRoute>} />
        <Route path="library" element={<PermissionRoute permission="LIBRARY"><SharedMediaLibraryPage /></PermissionRoute>} />
        <Route path="notes" element={<PermissionRoute permission="NOTES"><NotesPage /></PermissionRoute>} />
        <Route path="learning-status" element={<PermissionRoute permission="LEARNING_STATUS"><LearningStatusPage /></PermissionRoute>} />
        <Route path="data-stats" element={<PermissionRoute permission="DATA_STATS"><DataStatsPage /></PermissionRoute>} />
        <Route path="roles" element={<SuperRoute><RoleManagement /></SuperRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
